import React from 'react';
import { Box, Typography, Alert, CircularProgress } from '@mui/material';
import {
  People as PeopleIcon,
  AccountBalance as LoanIcon,
  Description as ApplicationIcon,
  TrendingUp as TrendingIcon,
  MonetizationOn as MoneyIcon,
  Warning as WarningIcon,
  Payment as PaymentIcon,
  AttachMoney as DisbursementIcon,
} from '@mui/icons-material';
import { useDashboardData } from '../../hooks/dashboard/useDashboardData';
import StatCard from '../../components/dashboard/StatCard';
import RecentActivities from '../../components/dashboard/RecentActivities';
import LoanDistributionChart from '../../components/dashboard/LoanDistributionChart';
import CollectionsChart from '../../components/dashboard/CollectionsChart';
import QuickActions from '../../components/dashboard/QuickActions';
import AlertsPanel from '../../components/dashboard/AlertsPanel';
import { useAuth } from '../../hooks/auth/useAuth';

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency: 'KES',
    minimumFractionDigits: 0,
  }).format(amount);
};

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { data, isLoading, error, refetch } = useDashboardData();

  // Handle error state
  if (error) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Dashboard
        </Typography>
        <Alert severity="error" sx={{ mt: 2 }}>
          Failed to load dashboard data. {error instanceof Error ? error.message : 'Please try again.'}
        </Alert>
      </Box>
    );
  }

  // Handle loading state for first load
  if (isLoading && !data) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 3 }} color="text.secondary">
          Loading dashboard...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Welcome back, {user?.fullName || user?.username}! Here's your overview.
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
          },
          gap: 3,
          mb: 4,
        }}
      >
        <StatCard
          title="Total Customers"
          value={data?.totalCustomers?.toLocaleString() || '0'}
          icon={<PeopleIcon />}
          color="#1976d2"
          isLoading={isLoading}
          subtitle="Registered customers"
        />
        <StatCard
          title="Active Loans"
          value={data?.totalActiveLoans?.toLocaleString() || '0'}
          icon={<LoanIcon />}
          color="#2e7d32"
          isLoading={isLoading}
          subtitle="Currently active"
        />
        <StatCard
          title="Pending Applications"
          value={data?.pendingApplications?.toLocaleString() || '0'}
          icon={<ApplicationIcon />}
          color="#ed6c02"
          isLoading={isLoading}
          subtitle="Awaiting review"
        />
        <StatCard
          title="Collections Today"
          value={formatCurrency(data?.collectedToday || 0)}
          icon={<TrendingIcon />}
          color="#9c27b0"
          isLoading={isLoading}
        />
      </Box>

      {/* Secondary Statistics */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
          },
          gap: 3,
          mb: 4,
        }}
      >
        <StatCard
          title="Total Portfolio"
          value={formatCurrency(data?.totalOutstanding || 0)}
          icon={<MoneyIcon />}
          color="#0288d1"
          isLoading={isLoading}
          subtitle="Outstanding value"
        />
        <StatCard
          title="Overdue Loans"
          value={data?.overdueLoans?.toLocaleString() || '0'}
          icon={<WarningIcon />}
          color="#d32f2f"
          isLoading={isLoading}
          subtitle="Requires attention"
        />
        <StatCard
          title="Total Disbursed"
          value={formatCurrency(data?.totalDisbursed || 0)}
          icon={<DisbursementIcon />}
          color="#388e3c"
          isLoading={isLoading}
          subtitle="All time"
        />
        <StatCard
          title="Collections (MTD)"
          value={formatCurrency(data?.collectedThisMonth || 0)}
          icon={<PaymentIcon />}
          color="#7b1fa2"
          isLoading={isLoading}
          subtitle="This month"
        />
      </Box>

      {/* Quick Actions */}
      <Box sx={{ mb: 4 }}>
        <QuickActions onRefresh={() => refetch()} />
      </Box>

      {/* Charts Section */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: 'repeat(2, 1fr)',
          },
          gap: 3,
          mb: 4,
        }}
      >
        <CollectionsChart
          data={data?.collectionTrends}
          isLoading={isLoading}
        />
        <LoanDistributionChart
          data={data?.loanDistribution}
          isLoading={isLoading}
        />
      </Box>

      {/* Recent Activities and Alerts */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: 'repeat(2, 1fr)',
          },
          gap: 3,
        }}
      >
        <RecentActivities
          loans={data?.recentLoans}
          applications={data?.recentApplications}
          payments={data?.recentPayments}
          isLoading={isLoading}
        />
        <AlertsPanel
          overdueAlerts={data?.overdueAlerts}
          pendingApprovals={data?.pendingApprovals}
          isLoading={isLoading}
        />
      </Box>
    </Box>
  );
};

export default Dashboard;
